import { Box, List, ListItem, Stack } from '@mui/joy'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from 'effector-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from '~/shared/lib/logger'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { Alert } from '~/shared/ui/Alerts'
import { Button } from '~/shared/ui/Buttons'
import { UploadIcon } from '~/shared/ui/Icons'
import { Modal, useModal } from '~/shared/ui/Modal'
import { Documents } from './Documents'
import { $importStatus, osgopImageMassImportFx } from './model'

type ImportOsgopImageTemplateProps = {
  isShow: boolean
  onCancel: () => void
}

export function ImportOsgopImageTemplate({
  isShow,
  onCancel,
}: ImportOsgopImageTemplateProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isSubmitting = useStore(osgopImageMassImportFx.pending)
  const importStatus = useStore($importStatus)
  const [file, setFile] = useState<File>()
  const [importErrors, setImportErrors] = useState<string[]>([])

  const {
    openModal: errorModalOpen,
    closeModal: errorModalClose,
    isModalShow: isErrorModalShow,
  } = useModal()

  const handleFileSet = (files?: File[]) => {
    setFile(files?.[0])
  }

  const handleCancel = () => {
    setFile(undefined)
    onCancel()
  }

  const handleSave = async () => {
    if (!file) {
      snackbarEnqueued({
        messageI18n: 'alert.upload file for import',
        variant: 'warning',
      })
      return
    }

    try {
      const result = await osgopImageMassImportFx(file)

      if (result.errors.length > 0) {
        setImportErrors(result.errors)
        errorModalOpen()
      } else {
        snackbarEnqueued({
          messageI18n: 'alert.osgop images are successfully imported',
          variant: 'success',
        })
        await queryClient.invalidateQueries({
          queryKey: ['cars'],
        })
        handleCancel()
      }
    } catch (e) {
      logger.error(e)
    }
  }

  const handleErrorModalClose = () => {
    errorModalClose()
    setImportErrors([])
  }

  return (
    <>
      <Modal
        isShow={isShow}
        onCancel={isSubmitting ? undefined : handleCancel}
        dialogProps={{
          sx: {
            minWidth: '800px',
            maxWidth: '800px',
          },
        }}
        title={t('osgop image list upload in progress')}
      >
        <Stack spacing={2}>
          <Box
            sx={{
              '.MuiGrid-direction-xs-row': {
                width: '100%',
              },
            }}
          >
            <Documents
              isShowUploadFiles
              onUploadFiles={handleFileSet}
              isSubmitting={isSubmitting}
              files={file ? [file] : []}
            />
          </Box>

          {importStatus === 'uploading' && (
            <Alert variant='info'>
              {t('alert.uploading file please wait')}
            </Alert>
          )}
          {(importStatus === 'pending' || importStatus === 'processing') && (
            <Alert variant='info'>
              {t(
                'alert.please wait until the import process is complete this may take some time',
              )}
            </Alert>
          )}
          <Stack direction='row' justifyContent='space-between' spacing={2}>
            <Button
              variant='gray'
              fullWidth
              disabled={isSubmitting}
              onClick={onCancel}
            >
              {t('button.close')}
            </Button>
            <Button
              fullWidth
              variant='brand'
              startDecorator={<UploadIcon />}
              onClick={handleSave}
              loading={isSubmitting}
            >
              {t('button.upload osgop images')}
            </Button>
          </Stack>
        </Stack>
      </Modal>

      <Modal
        isShow={isErrorModalShow}
        onCancel={handleErrorModalClose}
        title={t('import process error')}
      >
        <Stack spacing={2}>
          <Alert variant='error'>
            {t(
              'alert.errors were found in the uploaded import file please fix the errors and try uploading again',
            )}
          </Alert>
          <List
            sx={{
              maxHeight: '300px',
              overflowY: 'auto',
              fontSize: '14px',
            }}
          >
            {importErrors.map((error) => (
              <ListItem key={error}>{error}</ListItem>
            ))}
          </List>
          <Button variant='gray' onClick={handleErrorModalClose}>
            {t('button.close')}
          </Button>
        </Stack>
      </Modal>
    </>
  )
}
